import type { Field, FieldTypeMapping } from '@evidia/components';
import { SimpleForm } from '@evidia/components';
import { Alert } from 'antd';
import { useState } from 'react';
import { Delimiter } from '../../components/Delimiter';
import { AuthLayout } from '../../components/Layouts/AuthLayout/AuthLayout';
import Logo from '../../components/Logo';
import { SocialButtons } from '../../components/SocialButtons';
import { RULE_USER_EMAIL, RULE_USER_PASSWORD } from '../../const/forms';
import { ROUTES } from '../../const/routes';
import { useSetUserToken } from '../../hooks/useSetUserToken';
import registerImage from '../../images/register-photo.jpg';
import { trpc } from '../../utils/trcp';
import { LinkContainer, LogoWrapper, MissingPasswordLink, Paragraph, StyledLink, Title } from './styles';

const SHOW_SOCIAL_BUTTONS = false;

const FIELDS = [
	{
		key: 'email',
		label: 'E-mail',
		type: 'string',
		required: true,
		placeholder: 'evidiaonline@gmail.com',
		rules: [RULE_USER_EMAIL],
	},
	{
		key: 'password',
		label: 'Heslo',
		placeholder: 'Min. 6 písmen a speciální znak',
		type: 'password',
		required: true,
		rules: [RULE_USER_PASSWORD],
		footer: <MissingPasswordLink to={ROUTES.OUT_APP.RESET_PASSWORD}>Zapomněli jste heslo?</MissingPasswordLink>,
	},
] as const satisfies Field[];

type OnFinishType = FieldTypeMapping<typeof FIELDS>;

const LoginPage: React.FC = () => {
	const setToken = useSetUserToken();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<undefined | string>(undefined);

	const loginUser = trpc.user.loginUser.useMutation();

	const onFinishLocal = async (values: OnFinishType): Promise<void> => {
		setError(undefined);
		setLoading(true);

		try {
			const r = await loginUser.mutateAsync({
				email: values.email.toLowerCase().trim(),
				password: values.password.trim(),
			});

			if (r.state === 'OK') {
				setToken(r.token ?? undefined);
				return;
			}
			if (r.state === 'NOT_ACTIVATED') {
				setError('Účet není aktivován. Aktivujte ho prosím přes odkaz v e-mailu.');
			} else {
				setError('Nesprávné přihlašovací údaje.');
			}
		} catch {
			setError('Nesprávné přihlašovací údaje.');
		}

		setLoading(false);
	};

	return (
		<AuthLayout imageUrl={registerImage} imageDescription="Evidia lektor v autě">
			<LogoWrapper>
				<Logo />
			</LogoWrapper>
			<Title>
				Méně papírů,
				<br />
				více času na jízdy
			</Title>
			<Paragraph>Šetřete čas s automatizovanou evidencí</Paragraph>
			{SHOW_SOCIAL_BUTTONS && (
				<>
					<SocialButtons />
					<Delimiter>nebo</Delimiter>
				</>
			)}
			{error && <Alert message={error} type="error" style={{ textAlign: 'center', margin: '0.5em' }} />}
			<SimpleForm
				lessSpaceBetweenItems
				fields={FIELDS}
				onFinish={onFinishLocal}
				submitButtonTitle="Přihlásit se"
				loading={loading}
			/>
			<LinkContainer>
				Nemáte účet?&nbsp;
				<StyledLink to={ROUTES.OUT_APP.REGISTER}>Zaregistrujte se.</StyledLink>
			</LinkContainer>
		</AuthLayout>
	);
};

export default LoginPage;
